const railInstances = new WeakMap();

export class RyoxzelRail {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-ryoxzel-rail-viewport]');
    this.track = section.querySelector('[data-ryoxzel-rail-track]');
    this.cards = [...section.querySelectorAll('[data-ryoxzel-rail-card]')];
    this.previousButtons = [...section.querySelectorAll('[data-ryoxzel-rail-previous]')];
    this.nextButtons = [...section.querySelectorAll('[data-ryoxzel-rail-next]')];
    this.controls = [...section.querySelectorAll('[data-ryoxzel-rail-controls]')];
    this.progress = section.querySelector('[data-ryoxzel-rail-progress]');
    this.counter = section.querySelector('[data-ryoxzel-rail-count]');
    this.abortController = new AbortController();
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.duration = Math.min(820, Math.max(560, Number(section.dataset.duration) || 560));
    this.animationFrame = 0;
    this.motionVelocity = 0;
    this.updateFrame = 0;
    this.suppressClick = false;
    this.destroyed = false;
    this.observedWidths = new WeakMap();

    if (!this.viewport || !this.track) return;
    const { signal } = this.abortController;
    this.previousButtons.forEach((button) => button.addEventListener('click', () => this.move(-1), { signal }));
    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.move(1), { signal }));
    this.viewport.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
    this.viewport.addEventListener('wheel', () => this.interrupt(), { passive: true, signal });
    this.viewport.addEventListener('touchstart', () => this.interrupt(), { passive: true, signal });
    this.viewport.addEventListener('pointerdown', (event) => this.startDrag(event), { signal });
    this.viewport.addEventListener('pointermove', (event) => this.dragMove(event), { signal });
    this.viewport.addEventListener('pointerup', () => this.endDrag(), { signal });
    this.viewport.addEventListener('pointercancel', () => this.endDrag(), { signal });
    this.viewport.addEventListener('lostpointercapture', () => this.endDrag(), { signal });
    this.viewport.addEventListener('pointerleave', () => {
      if (this.dragState && !this.dragState.moved) this.dragState = null;
    }, { signal });
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.viewport.addEventListener('click', (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true, signal });
    this.viewport.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    section.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest?.('[data-ryoxzel-rail-card]');
      if (card) this.animate(this.cardPosition(card));
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.interrupt();
    }, { signal });
    this.reducedMotion.addEventListener('change', () => this.interrupt(), { signal });
    this.resizeObserver = new ResizeObserver((entries) => {
      const widthChanged = entries.some((entry) => {
        const width = entry.contentRect.width;
        const previousWidth = this.observedWidths.get(entry.target);
        this.observedWidths.set(entry.target, width);
        return previousWidth !== undefined && Math.abs(previousWidth - width) > .5;
      });
      if (widthChanged) this.interrupt();
      this.scheduleUpdate();
    });
    this.resizeObserver.observe(this.viewport);
    this.cards.forEach((card) => this.resizeObserver.observe(card));
    this.update();
  }

  maxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  cardPosition(card) {
    const firstCard = this.cards[0];
    if (!firstCard) return 0;
    const position = card.offsetLeft - firstCard.offsetLeft;
    return Math.max(0, Math.min(this.maxScroll(), position));
  }

  positions() {
    return [...new Set(this.cards.map((card) => this.cardPosition(card)))].sort((a, b) => a - b);
  }

  move(direction) {
    const current = this.animationFrame ? this.target : this.viewport.scrollLeft;
    const positions = this.positions();
    const target = direction > 0
      ? positions.find((position) => position > current + 1)
      : positions.findLast((position) => position < current - 1);
    if (target !== undefined) this.animate(target, this.animationFrame ? this.motionVelocity : 0);
  }

  interrupt() {
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
    this.motionVelocity = 0;
    this.target = this.viewport.scrollLeft;
    this.viewport.style.scrollSnapType = '';
  }

  animate(target, initialVelocity = 0) {
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
    this.target = Math.max(0, Math.min(this.maxScroll(), target));
    if (this.reducedMotion.matches) {
      this.viewport.scrollLeft = this.target;
      this.motionVelocity = 0;
      this.update();
      return;
    }
    this.motionVelocity = initialVelocity;
    let previousTime;
    let elapsed = 0;
    this.viewport.style.scrollSnapType = 'none';
    const tick = (time) => {
      if (this.destroyed) return;
      previousTime ??= time;
      const frameScale = Math.min(2, Math.max(.5, (time - previousTime) / (1000 / 60) || 1));
      previousTime = time;
      elapsed += (1000 / 60) * frameScale;
      const distance = this.target - this.viewport.scrollLeft;
      const attraction = distance * .032 * frameScale;
      this.motionVelocity = (this.motionVelocity + attraction) * (.74 ** frameScale);
      this.viewport.scrollLeft += this.motionVelocity * frameScale;
      const settled = Math.abs(distance) < .35 && Math.abs(this.motionVelocity) < .08;
      if (!settled && elapsed < this.duration) {
        this.animationFrame = requestAnimationFrame(tick);
      } else {
        this.viewport.scrollLeft = this.target;
        this.animationFrame = 0;
        this.motionVelocity = 0;
        this.viewport.style.scrollSnapType = '';
        this.update();
      }
    };
    this.animationFrame = requestAnimationFrame(tick);
  }

  startDrag(event) {
    this.suppressClick = false;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest?.('[data-ryoxzel-rail-no-drag], input, select, textarea, label')) return;
    this.interrupt();
    this.dragState = {
      x: event.clientX,
      lastX: event.clientX,
      lastTime: Number.isFinite(event.timeStamp) ? event.timeStamp : null,
      left: this.viewport.scrollLeft,
      pointerId: event.pointerId,
      velocity: 0,
      moved: false,
    };
  }

  dragMove(event) {
    if (!this.dragState) return;
    const delta = event.clientX - this.dragState.x;
    if (Math.abs(delta) <= 6 && !this.dragState.moved) return;
    this.dragState.moved = true;
    if (!this.viewport.hasPointerCapture(event.pointerId)) this.viewport.setPointerCapture(event.pointerId);
    this.viewport.dataset.dragging = '';
    this.viewport.style.scrollSnapType = 'none';
    this.viewport.scrollLeft = this.dragState.left - delta;
    if (this.dragState.lastTime !== null && Number.isFinite(event.timeStamp)) {
      const elapsed = Math.max(1, event.timeStamp - this.dragState.lastTime);
      const instantVelocity = -(event.clientX - this.dragState.lastX) / elapsed * (1000 / 60);
      this.dragState.velocity = this.dragState.velocity * .65 + instantVelocity * .35;
    }
    this.dragState.lastX = event.clientX;
    this.dragState.lastTime = Number.isFinite(event.timeStamp) ? event.timeStamp : this.dragState.lastTime;
    event.preventDefault();
  }

  endDrag() {
    if (!this.dragState) return;
    const { moved, pointerId, velocity } = this.dragState;
    this.dragState = null;
    delete this.viewport.dataset.dragging;
    if (this.viewport.hasPointerCapture(pointerId)) this.viewport.releasePointerCapture(pointerId);
    if (!moved) return;
    this.suppressClick = true;
    clearTimeout(this.clickTimer);
    this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
    const current = this.viewport.scrollLeft;
    const projected = Math.max(0, Math.min(this.maxScroll(), current + velocity * 11));
    const target = this.positions().reduce((nearest, position) => (
      Math.abs(nearest - projected) < Math.abs(position - projected) ? nearest : position
    ), 0);
    this.animate(target, velocity);
  }

  handleKeydown(event) {
    if (event.target !== this.viewport) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.animate(event.key === 'Home' ? 0 : this.maxScroll());
    }
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.update());
  }

  updateActiveCard() {
    if (!this.section.hasAttribute('data-ryoxzel-rail-active') || !this.cards.length) return;
    const viewportRect = this.viewport.getBoundingClientRect();
    const center = viewportRect.left + viewportRect.width / 2;
    const activeCard = this.cards.reduce((nearest, card) => {
      const nearestRect = nearest.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      return Math.abs(cardRect.left + cardRect.width / 2 - center) < Math.abs(nearestRect.left + nearestRect.width / 2 - center) ? card : nearest;
    }, this.cards[0]);
    this.cards.forEach((card) => card.classList.toggle('is-active', card === activeCard));
  }

  update() {
    if (this.destroyed) return;
    const max = this.maxScroll();
    const scrollable = this.cards.length > 1 && max > 1;
    this.controls.forEach((control) => { control.hidden = !scrollable; });
    this.previousButtons.forEach((button) => { button.disabled = !scrollable || this.viewport.scrollLeft <= 1; });
    this.nextButtons.forEach((button) => { button.disabled = !scrollable || this.viewport.scrollLeft >= max - 1; });
    this.viewport.tabIndex = scrollable ? 0 : -1;
    this.updateActiveCard();
    if (this.progress) {
      const share = Math.min(1, this.viewport.clientWidth / (this.viewport.scrollWidth || 1));
      const ratio = max ? Math.max(0, Math.min(1, this.viewport.scrollLeft / max)) : 0;
      this.progress.style.width = `${share * 100}%`;
      this.progress.style.transform = `translateX(${share ? ratio * (1 - share) / share * 100 : 0}%)`;
    }
    if (this.counter) {
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      const firstVisible = this.cards.findIndex((card) => card.getBoundingClientRect().right > viewportLeft + 2);
      const label = `${String(Math.max(0, firstVisible) + 1).padStart(2, '0')} / ${String(this.cards.length).padStart(2, '0')}`;
      if (this.counter.textContent !== label) this.counter.textContent = label;
    }
  }

  destroy() {
    this.destroyed = true;
    this.endDrag();
    this.interrupt();
    cancelAnimationFrame(this.updateFrame);
    clearTimeout(this.clickTimer);
    this.resizeObserver?.disconnect();
    this.abortController.abort();
  }
}

function findRails(root) {
  return [...(root.matches?.('[data-ryoxzel-rail]') ? [root] : []), ...root.querySelectorAll('[data-ryoxzel-rail]')];
}

function init(root) {
  findRails(root).forEach((section) => {
    if (!railInstances.has(section)) railInstances.set(section, new RyoxzelRail(section));
  });
}

function destroy(root) {
  findRails(root).forEach((section) => {
    railInstances.get(section)?.destroy();
    railInstances.delete(section);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
else init(document);
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
